import React from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface HealthScoreProps {
  resilienceScore: number;
  carbonReduction: number;
  supplierHealth: number;
  inventoryEfficiency: number;
  isLoading?: boolean;
  previousScore?: number;
}

/**
 * Supply Chain Health Score - The "Credit Score" for your supply chain
 * 
 * Consolidates complex BI metrics into a single, fluctuating number (0-100)
 * that triggers dopamine when users see it increase.
 */
const SupplyChainHealthScore: React.FC<HealthScoreProps> = ({
  resilienceScore = 0,
  carbonReduction = 0,
  supplierHealth = 0,
  inventoryEfficiency = 0,
  isLoading = false,
  previousScore,
}) => {
  // Calculate overall health score (0-100)
  const calculateHealthScore = (): number => {
    const weights = {
      resilience: 0.35,
      carbon: 0.20,
      supplier: 0.25,
      inventory: 0.20,
    };

    const score = 
      (resilienceScore * 100 * weights.resilience) +
      (Math.min(carbonReduction + 50, 100) * weights.carbon) + // Normalize carbon reduction
      (supplierHealth * weights.supplier) +
      (inventoryEfficiency * weights.inventory);

    return Math.round(Math.max(0, Math.min(100, score)));
  };

  const healthScore = calculateHealthScore();
  const scoreDiff = previousScore ? healthScore - previousScore : 0;
  
  // Get grade and color based on score
  const getGrade = (score: number): { grade: string; color: string; label: string } => {
    if (score >= 90) return { grade: 'A+', color: '#10B981', label: 'Excellent' };
    if (score >= 80) return { grade: 'A', color: '#34D399', label: 'Great' };
    if (score >= 70) return { grade: 'B', color: '#60A5FA', label: 'Good' };
    if (score >= 60) return { grade: 'C', color: '#FBBF24', label: 'Fair' };
    if (score >= 50) return { grade: 'D', color: '#F97316', label: 'Needs Work' };
    return { grade: 'F', color: '#EF4444', label: 'Critical' };
  };

  const { grade, color, label } = getGrade(healthScore);

  // Calculate stroke dashoffset for the circular progress
  const circumference = 2 * Math.PI * 45; // radius = 45
  const strokeDashoffset = circumference - (healthScore / 100) * circumference;

  if (isLoading) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: 3,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Glow effect */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${color}33 0%, transparent 70%)`,
          filter: 'blur(20px)',
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" color="white" fontWeight="bold">
          Supply Chain Health
        </Typography>
        <Tooltip title="Your supply chain health score combines resilience, sustainability, supplier performance, and inventory efficiency into one easy-to-track number.">
          <IconButton size="small" sx={{ color: 'grey.400' }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {/* Circular Score Gauge */}
        <Box sx={{ position: 'relative', width: 140, height: 140 }}>
          <svg width="140" height="140" style={{ transform: 'rotate(-90deg)' }}>
            {/* Background circle */}
            <circle
              cx="70"
              cy="70"
              r="45"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="12"
            />
            {/* Progress circle */}
            <circle
              cx="70"
              cy="70"
              r="45"
              fill="none"
              stroke={color}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              style={{
                transition: 'stroke-dashoffset 1s ease-in-out, stroke 0.5s ease',
              }}
            />
          </svg>
          {/* Score text in center */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}
          >
            <Typography variant="h3" color="white" fontWeight="bold">
              {healthScore}
            </Typography>
            <Typography variant="caption" sx={{ color: color, fontWeight: 'bold' }}>
              {grade}
            </Typography>
          </Box>
        </Box>

        {/* Score details */}
        <Box sx={{ ml: 3 }}>
          <Typography variant="body1" color={color} fontWeight="bold" gutterBottom>
            {label}
          </Typography>
          
          {scoreDiff !== 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {scoreDiff > 0 ? (
                <TrendingUpIcon sx={{ color: '#10B981', mr: 0.5 }} />
              ) : (
                <TrendingDownIcon sx={{ color: '#EF4444', mr: 0.5 }} />
              )}
              <Typography
                variant="body2"
                sx={{ color: scoreDiff > 0 ? '#10B981' : '#EF4444' }}
              >
                {scoreDiff > 0 ? '+' : ''}{scoreDiff} from last week
              </Typography>
            </Box>
          )}

          {/* Mini breakdown */}
          <Box sx={{ mt: 2 }}>
            <MiniMetric label="Resilience" value={Math.round(resilienceScore * 100)} />
            <MiniMetric label="Green Score" value={Math.min(Math.round(carbonReduction + 50), 100)} />
            <MiniMetric label="Suppliers" value={Math.round(supplierHealth)} />
            <MiniMetric label="Inventory" value={Math.round(inventoryEfficiency)} />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// Mini metric display component
const MiniMetric: React.FC<{ label: string; value: number }> = ({ label, value }) => {
  const getColor = (v: number) => {
    if (v >= 80) return '#10B981';
    if (v >= 60) return '#FBBF24';
    return '#EF4444';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: getColor(value),
          mr: 1,
        }}
      />
      <Typography variant="caption" color="grey.400" sx={{ minWidth: 70 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="white" fontWeight="bold">
        {value}%
      </Typography>
    </Box>
  );
};

export default SupplyChainHealthScore;
